import { AGENDA_STATUS_CONFIG } from "../ConfigFiles/agenda_status";
import type { 
    AgendaEntryResponse, 
    AgendaEntryFullResponse, 
    AgendaEntryCensoredResponse, 
    AgendaEntryBusyOnlyResponse 
} from '../Types/schema-helpers';

export type AnyAgendaEntry = 
  | AgendaEntryResponse 
  | AgendaEntryFullResponse 
  | AgendaEntryCensoredResponse 
  | AgendaEntryBusyOnlyResponse 
  | (Record<string, unknown> & { id?: string; _id?: string; start?: string; end?: string; status?: string });

export interface FullCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  timeZone: string;
  displayAttendee: string;
  extendedProps: AnyAgendaEntry;
  color?: string;
}

/**
 * Looks up the display properties for a given status slug.
 */
export const getStatusDisplay = (statusSlug?: string) => {
    const slug = statusSlug ? statusSlug.toUpperCase() : 'UNKNOWN';
    return AGENDA_STATUS_CONFIG[slug] || AGENDA_STATUS_CONFIG['UNKNOWN'];
};

/**
 * Transforms an array of API agenda entries into FullCalendar Event Objects.
 */
export const transformEvents = (apiEvents: AnyAgendaEntry[]): FullCalendarEvent[] => {
  if (!Array.isArray(apiEvents)) {
    return [];
  }

  return apiEvents.map(event => {
    const statusProps = getStatusDisplay(event.status);
    
    // Fallback logic for attendee resolution across Censored, Full, and Public response tiers
    let primaryAttendee = 'No Attendee';
    if ('attendees' in event && Array.isArray(event.attendees) && event.attendees.length > 0) {
      const firstAttendee = event.attendees[0] as { name?: string; username?: string } | undefined;
      primaryAttendee = firstAttendee?.name || firstAttendee?.username || 'No Attendee';
    }

    const eventId = (event as { id?: string; _id?: string }).id || (event as { id?: string; _id?: string })._id || '';
    
    // Explicit string type-guard prevents TS from inferring {} (unknown object)
    const rawTitle = 'title' in event && typeof event.title === 'string' ? event.title : '';
    const eventTitle: string = rawTitle || (event.status === 'BUSY' ? 'Reserved' : 'Appointment');

    return {
      id: String(eventId),
      title: eventTitle,
      start: typeof event.start === 'string' ? event.start : '',
      end: typeof event.end === 'string' ? event.end : '',
      timeZone: 'UTC',
      displayAttendee: primaryAttendee,
      extendedProps: event,
      color: statusProps?.color || '#3788d8',
    };
  });
};
